/**
 * Meanbook Note - Smart Order Parser Utility
 * ฟังก์ชันประมวลผลคำสั่งซื้อและที่อยู่อย่างละเอียด เพื่อประสานงานการจัดส่ง
 */

/**
 * 1. แปลงเบอร์โทรศัพท์เป็นรูปแบบมาตรฐาน 0xxxxxxxxx ไม่มีเว้นวรรคหรือขีด
 */
export function cleanPhoneNumber(rawPhone) {
  if (!rawPhone) return "";
  // ดึงเฉพาะตัวเลขและเครื่องหมายบวก
  let digits = rawPhone.replace(/[^\d\+]/g, "");
  
  if (digits.startsWith("+66")) {
    digits = "0" + digits.substring(3);
  } else if (digits.startsWith("66")) {
    digits = "0" + digits.substring(2);
  }
  
  return digits;
}

/**
 * 2. แปลงข้อความโน้ตดิบทั้งหมดให้กลายเป็นการ์ดออเดอร์ลูกค้าแบบละเอียด
 */
let addressDb = null;

/**
 * ฟังก์ชันรับฐานข้อมูลรหัสไปรษณีย์ประเทศแบบเรียลไทม์จากระบบภายนอก
 */
export function setAddressDatabase(db) {
  addressDb = db;
}

/**
 * ค้นหาช่วงดัชนีทั้งหมดของการจับคู่คำในข้อความดิบ
 */
function findAllMatchRanges(text, term, type) {
  if (!text || !term) return [];
  let pattern = "";
  const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  
  if (type === 'sub') {
    pattern = `(?:แขวง|ตำบล|ต\\.)?\\s*(${escapedTerm})`;
  } else if (type === 'dist') {
    if (term.startsWith("เมือง")) {
      pattern = `(?:เขต|อำเภอ|อ\\.)?\\s*(${escapedTerm})|(?:(?:เขต|อำเภอ|อ\\.)\\s*(เมือง))`;
    } else {
      pattern = `(?:เขต|อำเภอ|อ\\.)?\\s*(${escapedTerm})`;
    }
  } else if (type === 'prov') {
    if (term === "กรุงเทพมหานคร") {
      pattern = `(?:จังหวัด|จ\\.)?\\s*(กรุงเทพมหานครฯ|กรุงเทพมหานคร|กรุงเทพฯ|กรุงเทพ|กทม\\.?|กทม)`;
    } else {
      pattern = `(?:จังหวัด|จ\\.)?\\s*(${escapedTerm})`;
    }
  } else if (type === 'pc') {
    pattern = `\\b(${escapedTerm})\\b`;
  }
  
  const regex = new RegExp(pattern, 'gi');
  const ranges = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    ranges.push({
      start: match.index,
      end: match.index + match[0].length,
      matchedStr: match[0],
      termStr: match[1] || match[2] || match[0]
    });
    if (regex.lastIndex === match.index) {
      regex.lastIndex++;
    }
  }
  return ranges;
}

/**
 * หาการจับคู่ช่วงดัชนีที่ดีที่สุดโดยแต่ละฟิลด์ต้องไม่ทับซ้อนกัน
 */
function findBestDisjointRanges(subRanges, distRanges, provRanges, pcRanges) {
  let bestSelection = {
    sub: null,
    dist: null,
    prov: null,
    pc: null,
    score: -1,
    isOrdered: false
  };
  
  const sList = subRanges.length > 0 ? subRanges : [null];
  const dList = distRanges.length > 0 ? distRanges : [null];
  const pList = provRanges.length > 0 ? provRanges : [null];
  const cList = pcRanges.length > 0 ? pcRanges : [null];
  
  for (const s of sList) {
    for (const d of dList) {
      for (const p of pList) {
        for (const c of cList) {
          const selected = [s, d, p, c].filter(x => x !== null);
          let overlap = false;
          for (let i = 0; i < selected.length; i++) {
            for (let j = i + 1; j < selected.length; j++) {
              if (selected[i].start < selected[j].end && selected[j].start < selected[i].end) {
                overlap = true;
                break;
              }
            }
            if (overlap) break;
          }
          if (overlap) continue;
          
          let score = 0;
          let matchCount = 0;
          
          if (s) {
            score += 1.0;
            score += s.termStr.length * 0.01;
            matchCount++;
            if (/(แขวง|ตำบล|ต\.)/i.test(s.matchedStr)) {
              score += 2.0;
            }
          }
          
          if (d) {
            score += 1.0;
            matchCount++;
            if (/(เขต|อำเภอ|อ\.)/i.test(d.matchedStr)) {
              score += 2.0;
            }
          }
          
          if (p) {
            score += 0.5;
            matchCount++;
          }
          
          if (c) {
            score += 0.1;
            matchCount++;
          }
          
          // ตรวจสอบลำดับการเรียงตัว: sub < dist < prov < pc
          let isOrdered = true;
          const orderedPairs = [];
          if (s) orderedPairs.push({ type: 'sub', start: s.start });
          if (d) orderedPairs.push({ type: 'dist', start: d.start });
          if (p) orderedPairs.push({ type: 'prov', start: p.start });
          if (c) orderedPairs.push({ type: 'pc', start: c.start });
          
          for (let i = 0; i < orderedPairs.length - 1; i++) {
            if (orderedPairs[i].start >= orderedPairs[i+1].start) {
              isOrdered = false;
              break;
            }
          }
          
          if (isOrdered && matchCount >= 2) {
            score += 2.0; // โบนัสสำหรับการเรียงลำดับที่ถูกต้องตามโครงสร้างที่อยู่ไทย
          }
          
          if (score > bestSelection.score) {
            bestSelection = { sub: s, dist: d, prov: p, pc: c, score, isOrdered };
          }
        }
      }
    }
  }
  
  return bestSelection;
}

const STREET_BUILDING_PATTERN = /(?:ซอย|ซ\.|ถนน|ถ\.|ตรอก|หมู่บ้าน|ม\.|อาคาร|ตึก|คอนโด|หอพัก|หอ|ตลาด|วัด|สะพาน|แยก|โครงการ)\s*$/i;
const LANDMARK_PATTERN = /(?:ที่ว่าการ|ประจำ|ศาลากลาง|สถานีตำรวจ|สภ\.|สภ|สำนักงาน|ศาล|ขนส่ง|โรงพยาบาล|รพ\.|รพ|โรงเรียน|รร\.|รร|สาขา|กิ่ง|สนามกีฬา|กลาง)$/i;

function removeGeographicWord(addr, word, type, provinceName = null) {
  if (!word) return addr;
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  
  let isCapitalDistrict = false;
  if (type === 'dist' && provinceName && word === "เมือง" + provinceName) {
    isCapitalDistrict = true;
  }

  let regexStr = "";
  if (type === 'sub') {
    regexStr = `(?:(แขวง|ตำบล|ต\\.)?\\s*)(${escaped})`;
  } else if (type === 'dist') {
    if (isCapitalDistrict) {
      regexStr = `(?:(เขต|อำเภอ|อ\\.)?\\s*)(${escaped})|(?:(เขต|อำเภอ|อ\\.)\\s*)(เมือง)`;
    } else {
      regexStr = `(?:(เขต|อำเภอ|อ\\.)?\\s*)(${escaped})`;
    }
  } else if (type === 'prov') {
    if (word.includes("กรุงเทพ") || word.includes("กทม")) {
      regexStr = `(?:(จังหวัด|จ\\.)?\\s*)(กรุงเทพมหานครฯ|กรุงเทพมหานคร|กรุงเทพฯ|กรุงเทพ|กทม\\.?|กทม)`;
    } else {
      regexStr = `(?:(จังหวัด|จ\\.)?\\s*)(${escaped})`;
    }
  } else {
    regexStr = `()(${escaped})`;
  }

  const gRegex = new RegExp(regexStr, 'gi');
  let match;
  const matches = [];

  while ((match = gRegex.exec(addr)) !== null) {
    let prefix = "";
    let wordStr = "";
    if (isCapitalDistrict) {
      prefix = match[1] || match[3] || "";
      wordStr = match[2] || match[4];
    } else {
      prefix = match[1] || "";
      wordStr = match[2];
    }
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      prefix: prefix,
      matchStr: match[0],
      wordStr: wordStr
    });
    if (gRegex.lastIndex === match.index) {
      gRegex.lastIndex++;
    }
  }

  const validMatches = [];
  for (const m of matches) {
    const beforeText = addr.substring(0, m.start).trim();

    if (LANDMARK_PATTERN.test(beforeText)) {
      continue;
    }

    if (!m.prefix) {
      if (STREET_BUILDING_PATTERN.test(beforeText)) {
        continue;
      }
      if (type === 'dist' && /(จังหวัด|จ\.)$/i.test(beforeText)) {
        continue;
      }
      if (type === 'prov' && /(เขต|อำเภอ|อ\.|ตำบล|แขวง|ต\.)$/i.test(beforeText)) {
        continue;
      }
      if (type === 'sub' && /(เขต|อำเภอ|อ\.|จังหวัด|จ\.)$/i.test(beforeText)) {
        continue;
      }
    }

    validMatches.push(m);
  }

  if (validMatches.length === 0) return addr;

  const matchesWithPrefix = validMatches.filter(m => m.prefix && m.prefix.trim() !== "");
  const toRemove = matchesWithPrefix.length > 0 ? matchesWithPrefix : [validMatches[validMatches.length - 1]];
  toRemove.sort((a, b) => b.start - a.start);

  let result = addr;
  for (const m of toRemove) {
    result = result.substring(0, m.start) + result.substring(m.end);
  }

  return result;
}

/**
 * ฟังก์ชันกลางสำหรับวิเคราะห์แยกองค์ประกอบที่อยู่ (Smart Address Resolution Engine)
 */
export function resolveAddress(rawAddress, addressDatabase = null) {
  const dbToUse = addressDatabase || addressDb;
  let subDistrict = "";
  let district = "";
  let province = "";
  let postalCode = "";
  let addressBody = rawAddress || "";

  if (!rawAddress) {
    return { subDistrict, district, province, postalCode, addressBody };
  }

  // 1. ดึงรหัสไปรษณีย์ 5 หลัก
  const pcMatch = rawAddress.match(/\b(\d{5})\b/);
  if (pcMatch) {
    postalCode = pcMatch[1];
  }

  // สแกนหาตัวบอกพิกัดชัดเจนจากข้อความดิบ
  const sdMatch = rawAddress.match(/(?:แขวง|ตำบล|ต\.)\s*([^\s,]+)/);
  const dMatch = rawAddress.match(/(?:เขต|อำเภอ|อ\.)\s*([^\s,]+)/);
  const provMatch = rawAddress.match(/(?:จังหวัด|จ\.)\s*([^\s,]+)|(กรุงเทพมหานครฯ|กรุงเทพมหานคร|กรุงเทพฯ|กรุงเทพ|กทม\.?)/);

  let matchedFromDb = false;

  // 2. จับคู่อัตโนมัติจากฐานข้อมูล
  if (postalCode && dbToUse && dbToUse[postalCode]) {
    const candidates = dbToUse[postalCode];
    const flatSubs = [];
    for (const cand of candidates) {
      for (const sName of cand.s) {
        flatSubs.push({
          sName,
          dName: cand.d,
          pName: cand.p
        });
      }
    }

    let bestCandidate = null;
    let bestScore = -1;

    for (const item of flatSubs) {
      const subRanges = findAllMatchRanges(rawAddress, item.sName, 'sub');
      const distRanges = findAllMatchRanges(rawAddress, item.dName, 'dist');
      const provRanges = findAllMatchRanges(rawAddress, item.pName, 'prov');
      const pcRanges = findAllMatchRanges(rawAddress, postalCode, 'pc');

      const matchResult = findBestDisjointRanges(subRanges, distRanges, provRanges, pcRanges);
      const hasSub = (matchResult.sub !== null);
      if (hasSub && matchResult.score > bestScore) {
        bestScore = matchResult.score;
        bestCandidate = item;
      }
    }

    if (bestCandidate && bestScore > 0) {
      subDistrict = bestCandidate.sName;
      district = bestCandidate.dName;
      province = bestCandidate.pName;
      matchedFromDb = true;
    } else {
      if (candidates.length > 0) {
        let bestDistCand = null;
        let bestDistScore = -1;
        for (const cand of candidates) {
          let dScore = 0;
          const distRanges = findAllMatchRanges(rawAddress, cand.d, 'dist');
          if (distRanges.length > 0) dScore += 1.0;
          if (dScore > bestDistScore) {
            bestDistScore = dScore;
            bestDistCand = cand;
          }
        }

        const selectedCand = bestDistCand || candidates[0];
        province = selectedCand.p;
        district = selectedCand.d;

        if (sdMatch) {
          const userSub = sdMatch[1].trim();
          let foundInDb = false;
          for (const [pcKey, candList] of Object.entries(dbToUse)) {
            for (const c of candList) {
              if (c.p === province && (c.d === district || candList.length === 1)) {
                if (c.s.includes(userSub)) {
                  subDistrict = userSub;
                  foundInDb = true;
                  break;
                }
              }
            }
            if (foundInDb) break;
          }
          if (!subDistrict) {
            subDistrict = userSub;
          }
        } else if (selectedCand.s.length === 1) {
          subDistrict = selectedCand.s[0];
        }
        matchedFromDb = true;
      }
    }
  }

  // 3. ระบบแมตช์แบบดั้งเดิม (Regex Fallback) กรณีไม่ได้จับคู่จาก DB หรือไม่มีรหัสไปรษณีย์
  if (!matchedFromDb) {
    if (sdMatch) subDistrict = sdMatch[1];
    if (dMatch) district = dMatch[1];
    if (provMatch) province = provMatch[1] || provMatch[2];

    if (subDistrict && dbToUse) {
      for (const [pcKey, candList] of Object.entries(dbToUse)) {
        const found = candList.find(c => c.s.includes(subDistrict) && (!province || c.p === province));
        if (found) {
          if (!district) district = found.d;
          if (!province) province = found.p;
          if (!postalCode) postalCode = pcKey;
          break;
        }
      }
    }
  }

  // ล้างคำนำหน้า เช่น "ตำบล", "อำเภอ" ที่อาจติดมาใน regex fallback
  const cleanPrefix = (str, pattern) => str ? str.replace(pattern, "").trim() : "";
  subDistrict = cleanPrefix(subDistrict, /^(แขวง|ตำบล|ต\.)\s*/);
  district = cleanPrefix(district, /^(เขต|อำเภอ|อ\.)\s*/);
  province = cleanPrefix(province, /^(จังหวัด|จ\.)\s*/);

  // 4. ชะล้างและทำความสะอาดที่อยู่หลัก (Address Body Sanitizer)
  addressBody = rawAddress;
  if (postalCode) {
    addressBody = addressBody.replace(new RegExp(`\\b${postalCode}\\b`, 'g'), '');
  }

  if (subDistrict) addressBody = removeGeographicWord(addressBody, subDistrict, 'sub');
  if (district) addressBody = removeGeographicWord(addressBody, district, 'dist', province);
  if (province) {
    const isBkk = province.includes("กรุงเทพ") || province.includes("กทม");
    if (isBkk) {
      const bkkRegex = /(?:จังหวัด)?\s*(?:กรุงเทพมหานครฯ|กรุงเทพมหานคร|กรุงเทพฯ|กรุงเทพ|กทม\.?|กทม)/gi;
      addressBody = addressBody.replace(bkkRegex, '');
    } else {
      addressBody = removeGeographicWord(addressBody, province, 'prov');
    }
  }

  addressBody = addressBody
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*,+/g, ',')
    .replace(/\s*,\s*$/, '')
    .replace(/^[\s,\-\/\.]+/, '')
    .replace(/[\s,\-\/\.]+$/, '')
    .trim();

  return { subDistrict, district, province, postalCode, addressBody };
}

/**
 * 2. แปลงข้อความโน้ตดิบทั้งหมดให้กลายเป็นการ์ดออเดอร์ลูกค้าแบบละเอียด
 */
export function parseNoteContent(content) {
  if (!content) return [];
  const lines = content.split(/\r?\n/);
  const blocks = [];
  let currentBlock = null;

  // แบ่งข้อความออกเป็นบล็อกรายคน โดยใช้หมายเลขลำดับเป็นตัวตัด (เช่น "1.", "2.")
  for (let line of lines) {
    const match = line.match(/^\s*(\d+)\s*\.\s*(.+)$/);
    if (match) {
      if (currentBlock) {
        blocks.push(currentBlock);
      }
      currentBlock = {
        index: parseInt(match[1], 10),
        headerLine: match[2].trim(),
        lines: [line]
      };
    } else {
      if (currentBlock) {
        currentBlock.lines.push(line);
      }
    }
  }
  if (currentBlock) {
    blocks.push(currentBlock);
  }

  // นำบล็อกของแต่ละคนมาวิเคราะห์เจาะลึก
  return blocks.map(block => {
    const rawText = block.lines.join('\n');
    
    // แกะชื่อบัญชีลูกค้าในเพจ และแพลตฟอร์ม
    let customerName = block.headerLine;
    let platform = "ในเพจ"; // ค่าเริ่มต้น
    
    const platformMatch = block.headerLine.match(/(.+?)\s*(ในเเพจ|ในเพจ|ในเเเพจ|ใน\s*ig|ในไลน์|หน้าร้าน|ส่วนตัว|ในไอจี|ทางไลน์|ทางเพจ|เพจ|ไลน์|IG|Line|Facebook|Messenger)$/i);
    if (platformMatch) {
      customerName = platformMatch[1].trim();
      platform = platformMatch[2].trim();
    }

    const activeLines = block.lines.filter(l => l.trim() !== "");
    // เอาบรรทัดหัวข้อออก
    activeLines.shift(); 

    // บรรทัดแรกหลังหัวข้อ มักเป็นชื่อผู้รับในภาษาไทย/อังกฤษ
    let thaiName = "";
    if (activeLines.length > 0) {
      thaiName = activeLines[0].trim();
    }

    // แกะเบอร์โทรศัพท์
    let rawPhone = "";
    let phoneLineIndex = -1;
    for (let i = 0; i < activeLines.length; i++) {
      const line = activeLines[i];
      // ค้นหารูปแบบหมายเลขโทรศัพท์ 9-10 หลัก
      const phoneMatch = line.match(/(?:เบอร์|โทร|เบอร์โทร)?\s*(\+?66|0)\s*([0-9\-\s]{8,12})/);
      if (phoneMatch) {
        rawPhone = phoneMatch[0];
        phoneLineIndex = i;
        break;
      }
    }
    
    const phone = cleanPhoneNumber(rawPhone);

    // แยกแยะที่อยู่จัดส่ง
    const addressLines = [];

    // ระบุบรรทัดเริ่มต้นของที่อยู่หลัก (บรรทัดแรกที่ไม่ใช่ชื่อผู้รับ และไม่ใช่เบอร์โทรศัพท์)
    let startAddressIndex = -1;
    for (let i = 1; i < activeLines.length; i++) {
      if (i !== phoneLineIndex) {
        startAddressIndex = i;
        break;
      }
    }

    for (let i = 0; i < activeLines.length; i++) {
      if (i === 0) {
        continue; // ข้ามบรรทัดชื่อจริงผู้รับ
      }
      const line = activeLines[i];
      const isStatusLine = /^(จ่ายแล้ว|โอนแล้ว|จะมารับเอง|รับเอง|ส่งพร้อม|เรียกรถ|ส่งแมส|lalamove|grab|เลขพัสดุ|tracking|มัดจำ)/i.test(line.trim());

      if (i === phoneLineIndex) {
        // หากเบอร์โทรและที่อยู่รวมกันอยู่ในบรรทัดเดียวกัน
        const hasAddressCue = /\b\d{5}\b|ถนน|ซอย|ตำบล|อำเภอ|จังหวัด|แขวง|เขต|หมู่|ต\.|อ\.|จ\.|กรุงเทพ|กทม/.test(line);
        if (hasAddressCue && !isStatusLine) {
          const phonePattern = /(?:เบอร์|โทร|เบอร์โทร)?\s*(\+?66|0)\s*([0-9\-\s]{8,12})/i;
          const addrPart = line.replace(phonePattern, "").trim();
          if (addrPart) {
            addressLines.push(addrPart);
          }
        }
        continue;
      }

      if (isStatusLine) {
        continue; // ข้ามสถานะการจัดส่ง/ชำระเงิน
      }

      // บรรทัดที่อยู่คือบรรทัดแรกสุด หรือบรรทัดที่มีคีย์เวิร์ดระบุพิกัดที่อยู่ชัดเจน
      const hasAddressCue = /\b\d{5}\b|ถนน|ซอย|ตำบล|อำเภอ|จังหวัด|แขวง|เขต|หมู่|ต\.|อ\.|จ\.|กรุงเทพ|กทม/.test(line);
      const isAddress = (i === startAddressIndex) || hasAddressCue;

      if (isAddress) {
        addressLines.push(line.trim());
      }
    }

    // แยกแยะรายการสินค้าตามรอบส่ง (สูงสุด 3 รอบ แยกด้วยบรรทัดว่าง)
    const itemChunks = [];
    let currentChunk = [];

    const isNameLine = (l) => l.trim() === thaiName;
    const isPhoneLine = (l) => {
      const phoneMatch = l.match(/(?:เบอร์|โทร|เบอร์โทร)?\s*(\+?66|0)\s*([0-9\-\s]{8,12})/);
      return !!phoneMatch;
    };
    const isAddressLine = (l) => {
      const trimmed = l.trim();
      return addressLines.some(al => al.includes(trimmed) || trimmed.includes(al));
    };
    const isStatus = (l) => {
      return /^(จ่ายแล้ว|โอนแล้ว|จะมารับเอง|รับเอง|ส่งพร้อม|เรียกรถ|ส่งแมส|lalamove|grab|เลขพัสดุ|tracking|มัดจำ)/i.test(l.trim());
    };

    const contentLines = block.lines.slice(1);
    for (let l of contentLines) {
      const trimmed = l.trim();
      if (trimmed === "") {
        if (currentChunk.length > 0) {
          itemChunks.push(currentChunk.join('\n'));
          currentChunk = [];
        }
        continue;
      }

      if (isNameLine(l) || isPhoneLine(l) || isAddressLine(l) || isStatus(l)) {
        if (currentChunk.length > 0) {
          itemChunks.push(currentChunk.join('\n'));
          currentChunk = [];
        }
        continue;
      }

      // บรรทัดสินค้า
      const cleanItemMatch = l.match(/^\s*[-\*•]\s*(.+)$/);
      const cleanItem = cleanItemMatch ? cleanItemMatch[1].trim() : trimmed;
      currentChunk.push(cleanItem);
    }

    if (currentChunk.length > 0) {
      itemChunks.push(currentChunk.join('\n'));
    }

    const shipmentItems = itemChunks.slice(0, 3);
    const maxShipments = shipmentItems.length > 0 ? shipmentItems.length : 1;
    const items = shipmentItems.join('\n\n');

    const rawAddress = addressLines.join(' ');

    // แยกรายละเอียด แขวง/ตำบล เขต/อำเภอ จังหวัด รหัสไปรษณีย์ ออกจากที่อยู่หลักด้วยระบบอัจฉริยะ
    const resolved = resolveAddress(rawAddress, addressDb);
    const subDistrict = resolved.subDistrict;
    const district = resolved.district;
    const province = resolved.province;
    const postalCode = resolved.postalCode;
    const addressBody = resolved.addressBody;

    // ค้นหารูปแบบการจัดส่ง (Delivery Type)
    let deliveryType = 'express'; // ค่าเริ่มต้น: ส่งพัสดุ
    if (/จะมารับเอง|รับหน้าร้าน|รับเอง|มารับเอง/i.test(rawText)) {
      deliveryType = 'pickup';
    } else if (/เรียกรถ|ส่งรถ|lalamove|grab|ส่งแมส/i.test(rawText)) {
      deliveryType = 'messenger';
    }

    // ค้นหาสถานะชำระเงิน (Paid / Deposit / Unpaid)
    let isPaid = false;
    let paymentStatus = 'unpaid';
    let depositAmount = '';

    const depositMatch = rawText.match(/มัดจำ\s*(\d+(?:\.\d+)?)/i);
    if (depositMatch) {
      paymentStatus = 'deposit';
      depositAmount = depositMatch[1];
    } else if (/จ่ายแล้ว|โอนแล้ว|ชำระแล้ว/i.test(rawText)) {
      paymentStatus = 'paid';
      isPaid = true;
    }

    // ค้นหาเลขพัสดุเดิมในตัวหนังสือ (ถ้ามีอยู่แล้ว)
    let trackingNumber = "";
    const trackingMatch = rawText.match(/(?:เลขพัสดุ|เลขส่ง|tracking|track|พัสดุ)\s*[:\-]?\s*([A-Z0-9]{9,20})/i);
    if (trackingMatch) {
      trackingNumber = trackingMatch[1].trim();
    }

    // ค้นหาบันทึกย่อ เช่น ฝากส่งพร้อมเทอร์โบ
    let notes = "";
    const noteMatches = [];
    block.lines.forEach(l => {
      const trimmedLine = l.trim();
      if (/ส่งพร้อม|ฝากส่ง|รอส่ง|จ่ายแล้ว/i.test(trimmedLine)) {
        // เอาเรื่องจ่ายแล้วออกถ้าจะเขียนโน้ตเพิ่มเติมทั่วไป
        if (!/^(จ่ายแล้ว|โอนแล้ว)$/.test(trimmedLine)) {
          noteMatches.push(trimmedLine);
        }
      }
    });
    notes = noteMatches.join(', ');

    const cleanSub = (name) => {
      if (!name) return "";
      return name.replace(/^(แขวง|ตำบล|ต\.)\s*/, "").trim();
    };
    
    const cleanDist = (name) => {
      if (!name) return "";
      return name.replace(/^(เขต|อำเภอ|อ\.)\s*/, "").trim();
    };

    return {
      id: `${block.index}`,
      index: block.index,
      rawText,
      customerName,
      platform,
      thaiName,
      addressBody: addressBody || rawAddress,
      subDistrict: cleanSub(subDistrict),
      district: cleanDist(district),
      province,
      postalCode,
      phone,
      items,
      deliveryType,
      isPaid,
      paymentStatus,
      depositAmount,
      trackingNumber,
      notes,
      maxShipments,
      shipmentItems
    };
  });
}

/**
 * 3. ฟังก์ชันปรับปรุงข้อความในบล็อกลูกค้ารายคนตามความต้องการ (เช่น เพิ่มคำว่า จ่ายแล้ว หรือเลขพัสดุ)
 */
export function updateOrderText(rawText, field, value) {
  let lines = rawText.split(/\r?\n/);
  
  if (field === 'isPaid') {
    const isPaid = !!value;
    let hasPaymentLine = false;
    
    // ค้นหาคำที่เกี่ยวข้องและแก้ไข/ตัดออก
    lines = lines.map(line => {
      if (/จ่ายแล้ว|โอนแล้ว|ชำระแล้ว/i.test(line)) {
        hasPaymentLine = true;
        return isPaid ? line : line.replace(/จ่ายแล้ว|โอนแล้ว|ชำระแล้ว/gi, '').trim();
      }
      return line;
    });
    
    lines = lines.filter(line => line.trim() !== "");
    
    if (isPaid && !hasPaymentLine) {
      // แทรกสถานะ "จ่ายแล้ว" ไปที่บรรทัดสุดท้าย (ก่อนรายการสินค้าที่เป็นแดช - )
      const itemIndex = lines.findIndex(l => l.trim().startsWith('-') || l.trim().startsWith('*'));
      if (itemIndex !== -1) {
        lines.splice(itemIndex, 0, 'จ่ายแล้ว');
      } else {
        lines.push('จ่ายแล้ว');
      }
    }
  }
  
  if (field === 'deliveryType') {
    const type = value; // 'pickup', 'express', 'messenger'
    
    // กวาดล้างคำศัพท์ประเภทขนส่งเก่าออกก่อน
    lines = lines.map(line => {
      return line.replace(/จะมารับเอง|รับหน้าร้าน|รับเอง|มารับเอง|เรียกรถ|ส่งรถ|lalamove|grab|ส่งแมส/gi, '').trim();
    }).filter(line => line.trim() !== "");
    
    let phrase = "";
    if (type === 'pickup') phrase = "จะมารับเอง";
    else if (type === 'messenger') phrase = "เรียกรถ";
    // ถ้าเป็น express (ส่งพัสดุ) จะไม่มีคีย์เวิร์ดพิเศษเพิ่มเติม
    
    if (phrase) {
      const itemIndex = lines.findIndex(l => l.trim().startsWith('-') || l.trim().startsWith('*'));
      if (itemIndex !== -1) {
        lines.splice(itemIndex, 0, phrase);
      } else {
        lines.push(phrase);
      }
    }
  }

  if (field === 'trackingNumber') {
    const tracking = value ? value.trim() : "";
    
    // ค้นหาบรรทัดเลขพัสดุและลบออก
    lines = lines.map(line => {
      if (/เลขพัสดุ|เลขส่ง|tracking|track|พัสดุ/i.test(line)) {
        return "";
      }
      return line;
    }).filter(line => line.trim() !== "");
    
    if (tracking) {
      // เพิ่มเลขพัสดุไปบรรทัดสุดท้ายต่อจากสินค้า หรือต่อท้ายการ์ด
      lines.push(`เลขพัสดุ: ${tracking}`);
    }
  }
  
  return lines.join('\n');
}

/**
 * 4. ฟังก์ชันหลักในการแก้ไขข้อความโน้ตรวมทั้งไฟล์ โดยระบุเป้าหมายรายคนแล้วเขียนตัวอักษรกลับเข้าไป
 */
export function updateNoteContentWithOrder(noteContent, orderIndex, field, value) {
  if (!noteContent) return "";
  const lines = noteContent.split(/\r?\n/);
  const blocks = [];
  let currentBlock = null;

  // รวบรวมตำแหน่งดั้งเดิมในโน้ตดิบ
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(/^\s*(\d+)\s*\.\s*(.+)$/);
    if (match) {
      if (currentBlock) {
        currentBlock.endLine = i - 1;
        blocks.push(currentBlock);
      }
      currentBlock = {
        index: parseInt(match[1], 10),
        startLine: i,
        endLine: -1,
        lines: [line]
      };
    } else {
      if (currentBlock) {
        currentBlock.lines.push(line);
      }
    }
  }
  if (currentBlock) {
    currentBlock.endLine = lines.length - 1;
    blocks.push(currentBlock);
  }

  // ค้นหาลูกค้าเป้าหมาย
  const targetBlock = blocks.find(b => b.index === orderIndex);
  if (!targetBlock) return noteContent;

  const oldBlockText = targetBlock.lines.join('\n');
  const newBlockText = updateOrderText(oldBlockText, field, value);

  // สลับสับเปลี่ยนข้อความเฉพาะช่วงบรรทัดของลูกค้าคนนั้น ๆ
  const beforeLines = lines.slice(0, targetBlock.startLine);
  const afterLines = lines.slice(targetBlock.endLine + 1);

  return [...beforeLines, ...newBlockText.split('\n'), ...afterLines].join('\n');
}

/**
 * 2.5 แปลงข้อความโน้ตดิบของออเดอร์ด่วนเป็นออเดอร์ลูกค้าแบบละเอียด (Quick Order Parser)
 */
export function parseQuickOrderContent(content) {
  if (!content) return [];
  const lines = content.split(/\r?\n/);
  const blocks = [];
  let currentBlock = null;

  // แบ่งข้อความตามลำดับตัวเลขนำหน้า (เช่น "1. ", "2. ")
  for (let line of lines) {
    const match = line.match(/^\s*(\d+)\s*\.\s*(.+)$/);
    if (match) {
      if (currentBlock) {
        blocks.push(currentBlock);
      }
      currentBlock = {
        index: parseInt(match[1], 10),
        headerLine: match[2].trim(),
        lines: [line]
      };
    } else {
      if (currentBlock) {
        currentBlock.lines.push(line);
      }
    }
  }
  if (currentBlock) {
    blocks.push(currentBlock);
  }

  // หากไม่มีตัวเลขนำหน้า ให้ถือว่าเป็นบล็อกเดียว
  if (blocks.length === 0) {
    blocks.push({
      index: 1,
      headerLine: "",
      lines: lines
    });
  }

  return blocks.map(block => {
    // กรองบรรทัดว่างออก
    const activeLines = block.lines.map(l => l.trim()).filter(l => l !== "");
    if (activeLines.length === 0) return null;
    
    // บรรทัดแรกสุดของออเดอร์ด่วนคือ ชื่อจริงผู้รับ
    let firstLine = activeLines[0];
    let thaiName = "";
    if (firstLine.match(/^\s*\d+\s*\.\s*(.+)$/)) {
      thaiName = firstLine.match(/^\s*\d+\s*\.\s*(.+)$/)[1].trim();
    } else {
      thaiName = firstLine;
    }

    let phone = "";
    let phoneLineIndex = -1;
    let rawAddress = "";
    let addressLineIndex = -1;

    // 1. ค้นหาเบอร์โทรศัพท์จากทุกบรรทัด
    for (let i = 0; i < activeLines.length; i++) {
      const line = activeLines[i];
      const phoneMatch = line.match(/(?:เบอร์|โทร|เบอร์โทร)?\s*(\+?66|0)\s*([0-9\-\s]{8,12})/);
      if (phoneMatch) {
        phone = cleanPhoneNumber(phoneMatch[0]);
        phoneLineIndex = i;
        break;
      }
    }

    // 2. ค้นหาบรรทัดที่อยู่จัดส่ง
    // ที่อยู่คือบรรทัดแรกที่ไม่ใช่ชื่อผู้รับ (ดัชนี 0) และไม่ใช่เบอร์โทรศัพท์
    for (let i = 0; i < activeLines.length; i++) {
      if (i === 0) continue;
      
      const line = activeLines[i];
      
      if (i === phoneLineIndex) {
        // กรณีเบอร์โทรและที่อยู่รวมกันอยู่ในบรรทัดเดียวกัน
        const hasAddressCue = /\b\d{5}\b|ถนน|ซอย|ตำบล|อำเภอ|จังหวัด|แขวง|เขต|หมู่/.test(line);
        if (hasAddressCue) {
          const phonePattern = /(?:เบอร์|โทร|เบอร์โทร)?\s*(\+?66|0)\s*([0-9\-\s]{8,12})/i;
          rawAddress = line.replace(phonePattern, "").trim();
          addressLineIndex = i;
          break;
        }
      } else {
        rawAddress = line;
        addressLineIndex = i;
        break;
      }
    }

    // 3. บรรทัดที่เหลือหลังจากลบ ชื่อ, เบอร์โทร และที่อยู่ จะถือว่าเป็นรายการสินค้าทั้งหมด
    const items = [];
    for (let i = 0; i < activeLines.length; i++) {
      if (i === 0 || i === phoneLineIndex || i === addressLineIndex) {
        continue;
      }
      items.push(activeLines[i]);
    }

    // นำดึงที่อยู่ดิบ (rawAddress) ไปสแกนหา ตำบล อำเภอ จังหวัด รหัสไปรษณีย์ โดยใช้ resolveAddress เดียวกันกับออเดอร์หลัก
    const resolved = resolveAddress(rawAddress, addressDb);
    const subDistrict = resolved.subDistrict;
    const district = resolved.district;
    const province = resolved.province;
    const postalCode = resolved.postalCode;
    const addressBody = resolved.addressBody;

    return {
      id: `${block.index}`,
      index: block.index,
      rawText: activeLines.join('\n'),
      customerName: thaiName,
      platform: "",
      thaiName,
      addressBody: addressBody || rawAddress,
      subDistrict,
      district,
      province,
      postalCode,
      phone,
      items,
      deliveryType: 'express',
      isPaid: true,
      paymentStatus: 'paid',
      depositAmount: '',
      trackingNumber: '',
      notes: ''
    };
  }).filter(b => b !== null);
}
